import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

import Button from "../../../../components/buttons/Button";
import TextField from "../../../../components/input/textfield/TextField";
import TextArea from "../../../../components/input/textarea/TextArea";

import { generateGroupCode } from "../../../../utils/helpers/generator/generateGroupCode";
import { useCreatorGroups } from "../hooks/useCreatorGroups";

import "./CreatorCreateGroupForm.css";

const createInitialFormData = () => ({
  groupName: "",
  groupCode: generateGroupCode(6).toUpperCase(),
  groupPolicy: "",
});

export default function CreatorCreateGroupForm({
  username,
  onClose,
  onSuccess,
}) {
  const { loading, error, createCreatorGroup } = useCreatorGroups(username);

  const [formData, setFormData] = useState(createInitialFormData);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  const saving = loading || submitting;

  const groupName = formData.groupName.trim();
  const groupCode = formData.groupCode.trim();
  const groupPolicy = formData.groupPolicy.trim();

  const canSubmit =
    Boolean(username) &&
    Boolean(groupName) &&
    Boolean(groupCode) &&
    Boolean(groupPolicy) &&
    !saving;

  const errorMessage =
    formError || (typeof error === "string" ? error : error?.message || "");

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && !saving) {
        event.preventDefault();
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [saving, onClose]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));

    setFormError("");
  };

  const handleClose = () => {
    if (saving) return;
    onClose?.();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (saving) return;

    if (!username) {
      setFormError("Creator information is missing.");
      return;
    }

    if (!groupName) {
      setFormError("Group name is required.");
      return;
    }

    if (!groupCode) {
      setFormError("Group code is required.");
      return;
    }

    if (!groupPolicy) {
      setFormError("Group privacy and rules are required.");
      return;
    }

    setSubmitting(true);
    setFormError("");

    try {
      const result = await createCreatorGroup({
        groupName,
        groupCode,
        groupPolicy,
      });

      if (result === false || result?.success === false) {
        setFormError(result?.message || "Unable to create group.");
        return;
      }

      await onSuccess?.(result);
      onClose?.();
    } catch (submitError) {
      console.error("Unable to create group:", submitError);

      setFormError(
        submitError?.response?.data?.message ||
          submitError?.message ||
          "Unable to create group.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <form
      className="creator-create-group-form"
      onSubmit={handleSubmit}
      aria-label="Create Group"
    >
      <header className="creator-create-group-form__header">
        <div className="creator-create-group-form__header-info">
          <h2>Create Group</h2>
          <p>Create a group and manage your community.</p>
        </div>

        <button
          type="button"
          className="creator-create-group-form__close"
          onClick={handleClose}
          disabled={saving}
          aria-label="Close create group form"
        >
          <X size={20} />
        </button>
      </header>

      <div className="creator-create-group-form__workspace">
        <main className="creator-create-group-form__main">
          <div className="creator-create-group-form__section-header">
            <h3>Group Information</h3>
            <p>Provide information about your group.</p>
          </div>

          <div className="creator-create-group-form__fields">
            <TextField
              label="Group Name"
              name="groupName"
              value={formData.groupName}
              onChange={handleChange}
              placeholder="e.g. European Tour"
              maxLength={80}
              required
              disabled={saving}
            />

            <TextArea
              label="Group Privacy & Rules"
              name="groupPolicy"
              value={formData.groupPolicy}
              onChange={handleChange}
              placeholder="Explain the group rules and how members should use its content."
              maxLength={1000}
              maxHeight={180}
              required
              disabled={saving}
            />
          </div>
        </main>

        <aside className="creator-create-group-form__sidebar">
          <div className="creator-create-group-form__section-header">
            <h3>Group Settings</h3>
            <p>Manage your group's access information.</p>
          </div>

          <div className="creator-create-group-form__fields">
            <TextField
              label="Group Code"
              name="groupCode"
              value={formData.groupCode}
              placeholder="Group access code"
              disabled
            />

            <p className="creator-create-group-form__help">
              Share this code with people who want to join your group.
            </p>
          </div>

          <div className="creator-create-group-form__summary">
            <h4>Group Summary</h4>

            <div>
              <span>Name</span>
              <strong>{groupName || "—"}</strong>
            </div>

            <div>
              <span>Group Code</span>
              <strong>{groupCode}</strong>
            </div>

            <div>
              <span>Group Rules</span>
              <strong>{groupPolicy ? "Provided" : "Required"}</strong>
            </div>
          </div>
        </aside>
      </div>

      <footer className="creator-create-group-form__footer">
        <div className="creator-create-group-form__footer-status">
          {errorMessage ? (
            <span className="creator-create-group-form__error" role="alert">
              {errorMessage}
            </span>
          ) : saving ? (
            <span>Creating group...</span>
          ) : null}
        </div>

        <div className="creator-create-group-form__actions">
          <Button
            type="button"
            value="Cancel"
            variant="primary-outlined"
            onClick={handleClose}
            disabled={saving}
          />

          <Button
            type="submit"
            value={saving ? "Creating..." : "Create Group"}
            variant="primary"
            disabled={!canSubmit}
          />
        </div>
      </footer>
    </form>,
    document.body,
  );
}
